import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardComp, Loader } from "../../../../components";
import { BsArrowRight } from "react-icons/bs";
import {
  useGetDashboardQuery,
  useGetActiveCooperativesSubscriptionsQuery,
  useGetExpiringCooperativesSubscriptionsQuery,
  useGetExpiredCooperativesSubscriptionsQuery,
} from "../../../../store/auth/authApi";
import { formatMoney } from "../../../../utils";
// import {} from "react-icons/ri";
import EmptyTable from "../../../../components/Common/EmptyTable.jsx";

const DISMISSED_ALERTS_KEY = "unicoop_manager_dashboard_dismissed_alerts";

function readDismissedAlerts() {
  try {
    const raw = sessionStorage.getItem(DISMISSED_ALERTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function DashboardAlertBanner({
  alertKey,
  count,
  title,
  description,
  actionLabel,
  onAction,
  tone,
  dismissedMap,
  onDismiss,
}) {
  if (!count || count <= 0) return null;
  if (dismissedMap[`${alertKey}:${count}`]) return null;

  const tones = {
    amber: "border-amber-200 bg-amber-50",
    sky: "border-sky-200 bg-sky-50",
    violet: "border-violet-200 bg-violet-50",
  };

  return (
    <div
      className={`mb-6 rounded-xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3 relative ${
        tones[tone] || tones.amber
      }`}
      role="status"
    >
      <button
        type="button"
        onClick={() => onDismiss(alertKey, count)}
        className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-black/5 transition-colors"
        aria-label={`Dismiss ${title}`}
        title="Dismiss"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
      <div className="pr-8">
        <p className="font-semibold text-gray-900">{title}</p>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
      <button
        type="button"
        onClick={onAction}
        className="bg-[#8B313A] text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm font-medium shrink-0"
      >
        {actionLabel}
      </button>
    </div>
  );
}

export default function HomeComp() {
  const [activeSubscriptionTab, setActiveSubscriptionTab] = useState("active");
  const [dismissedAlerts, setDismissedAlerts] = useState(readDismissedAlerts);
  const navigate = useNavigate();

  const dismissAlert = useCallback((alertKey, count) => {
    setDismissedAlerts((prev) => {
      const next = { ...prev, [`${alertKey}:${count}`]: true };
      try {
        sessionStorage.setItem(DISMISSED_ALERTS_KEY, JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  }, []);

  // Log the response of getCurrentMemberProfile
  const { data } = useGetDashboardQuery();
  console.log("Dashboard summary data:", data?.data);
  const {
    data: activeSubscriptionsData,
    isLoading: activeSubscriptionsLoading,
    error: activeSubscriptionsError,
  } = useGetActiveCooperativesSubscriptionsQuery();

  if (
    activeSubscriptionsData ||
    activeSubscriptionsLoading ||
    activeSubscriptionsError
  ) {
    console.log("Active subscriptions: ", activeSubscriptionsData);
  }

  const {
    data: expiringSubscriptionsData,
    isLoading: expiringSubscriptionsLoading,
    error: expiringSubscriptionsError,
  } = useGetExpiringCooperativesSubscriptionsQuery();

  if (
    expiringSubscriptionsData ||
    expiringSubscriptionsLoading ||
    expiringSubscriptionsError
  ) {
    console.log("Expiring subscriptions: ", expiringSubscriptionsData);
  }

  const {
    data: expiredSubscriptionsData,
    isLoading: expiredSubscriptionsLoading,
    error: expiredSubscriptionsError,
  } = useGetExpiredCooperativesSubscriptionsQuery();

  if (
    expiredSubscriptionsData ||
    expiredSubscriptionsLoading ||
    expiredSubscriptionsError
  ) {
    console.log("Expired subscriptions: ", expiredSubscriptionsData);
  }

  // Use API data for subscriptions if available
  const activeSubscriptions = Array.isArray(activeSubscriptionsData?.data)
    ? activeSubscriptionsData.data.slice(0, 5)
    : [];

  const expiringSubscriptions = Array.isArray(expiringSubscriptionsData?.data)
    ? expiringSubscriptionsData.data.slice(0, 5)
    : [];

  const expiredSubscriptions = Array.isArray(expiredSubscriptionsData?.data)
    ? expiredSubscriptionsData.data.slice(0, 5)
    : [];

  const pendingRegistrationCount =
    data?.data?.pending_cooperative_registration_applications_count ?? 0;
  const pendingDemoCount = data?.data?.pending_demo_requests_count ?? 0;
  const pendingContactCount = data?.data?.pending_contact_submissions_count ?? 0;

  const alertCopy = useMemo(
    () => ({
      registration: {
        title: "New Get Started applications awaiting review",
        description: `${pendingRegistrationCount} pending application${
          pendingRegistrationCount !== 1 ? "s" : ""
        }.`,
      },
      demo: {
        title: "Demo requests awaiting follow-up",
        description: `${pendingDemoCount} pending request${
          pendingDemoCount !== 1 ? "s" : ""
        } from the marketing site.`,
      },
      contact: {
        title: "Contact messages awaiting reply",
        description: `${pendingContactCount} new message${
          pendingContactCount !== 1 ? "s" : ""
        } from the website contact form.`,
      },
    }),
    [pendingRegistrationCount, pendingDemoCount, pendingContactCount]
  );

  const renderSubscriptions = (subscriptions) => {
    console.log("subscriptions: ", subscriptions);
    return subscriptions.map((sub, index) => (
      <CardComp
        key={sub.id || index}
        cardClassName="border-0 shadow p-4 rounded-xl bg-white mb-4"
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <img
              src={sub.logo_url || "/Images/Dashboard/image1.svg"}
              alt="avatar"
              className="w-10 h-10 rounded-full mr-3"
            />
            <div>
              <p className="font-semibold text-lg text-gray-800">
                {sub.name || "-----"}
              </p>
              <p className="text-sm text-gray-500">{sub.address || "-----"}</p>
            </div>
          </div>
          <div className="flex items-center space-x-8">
            <div>
              <p className="text-xl font-semibold text-gray-800">
                {sub.total_branches !== undefined && sub.total_branches !== null
                  ? sub.total_branches
                  : "-----"}
              </p>
              <p className="text-sm text-gray-500">Branches</p>
            </div>
            <div>
              <p className="text-xl font-semibold text-gray-800">
                {sub.total_members !== undefined && sub.total_members !== null
                  ? sub.total_members
                  : "-----"}
              </p>
              <p className="text-sm text-gray-500">Total Member</p>
            </div>
            <div>
              <p className="text-[#19bc31] text-xl font-semibold">
                {sub.total_contributions_formatted ||
                  (sub.total_contributions !== undefined &&
                  sub.total_contributions !== null
                    ? formatMoney(sub.total_contributions)
                    : "-----")}
              </p>
              <p className="text-sm text-gray-500">Total Contributions</p>
            </div>
            <div>
              <p className="text-[#ff4343] text-xl font-semibold">
                {sub.total_loans_formatted ||
                  (sub.total_loans !== undefined && sub.total_loans !== null
                    ? formatMoney(sub.total_loans)
                    : "-----")}
              </p>
              <p className="text-sm text-gray-500">Loan Disbursed</p>
            </div>
            <BsArrowRight
              className="w-5 h-5 cursor-pointer"
              onClick={() =>
                navigate(`/cooperatives/${sub.company_id}`)
              }
            />
          </div>
        </div>
      </CardComp>
    ));
  };

  return (
    <div className="min-h-screen p-6">
      <div className="bg-white rounded-xl p-4 mb-6">
        <h1 className="text-2xl font-bold text-[#45464e]">Dashboard</h1>
      </div>

      <DashboardAlertBanner
        alertKey="registration"
        count={pendingRegistrationCount}
        title={alertCopy.registration.title}
        description={alertCopy.registration.description}
        actionLabel="Review applications"
        onAction={() => navigate("/registration-applications")}
        tone="amber"
        dismissedMap={dismissedAlerts}
        onDismiss={dismissAlert}
      />

      <DashboardAlertBanner
        alertKey="demo"
        count={pendingDemoCount}
        title={alertCopy.demo.title}
        description={alertCopy.demo.description}
        actionLabel="View demo requests"
        onAction={() => navigate("/demo-requests")}
        tone="sky"
        dismissedMap={dismissedAlerts}
        onDismiss={dismissAlert}
      />

      <DashboardAlertBanner
        alertKey="contact"
        count={pendingContactCount}
        title={alertCopy.contact.title}
        description={alertCopy.contact.description}
        actionLabel="View messages"
        onAction={() => navigate("/contact-submissions")}
        tone="violet"
        dismissedMap={dismissedAlerts}
        onDismiss={dismissAlert}
      />

      <div className="flex justify-between gap-4">
        <div className="w-3/5 space-y-6">
          <div className="flex items-center gap-4 bg-white rounded-xl p-4 justify-between">
            <div>
              <p className="text-sm text-gray-500">Cooperatives Onboarded</p>
              <p className="text-3xl font-bold text-gray-800">
                {data?.data?.totalCooperatives}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Branches</p>
              <p className="text-3xl font-bold text-gray-800">
                {data?.data?.totalBranches || data?.data?.total_branches || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Member</p>
              <p className="text-3xl font-bold text-gray-800">
                {data?.data?.totalMember ||
                  data?.data?.totalMembers ||
                  data?.data?.total_members ||
                  0}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Staff</p>
              <p className="text-3xl font-bold text-gray-800">
                {data?.data?.totalStaff ||
                  data?.data?.totalStaffs ||
                  data?.data?.total_staffs ||
                  0}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border-0 shadow p-6 rounded-xl bg-white flex items-center h-36">
              <div className="rounded-full w-14 h-14 bg-green-100 flex items-center justify-center mr-4">
                <img
                  src="/Images/Dashboard/money-recive.svg"
                  alt="naira"
                  className="w-7 h-7"
                />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">
                  {formatMoney(
                    data?.data?.totalContribution ||
                      data?.data?.totalContributions ||
                      data?.data?.total_contributions ||
                      0
                  )}
                </p>
                <p className="text-sm text-gray-500 mt-1">Total Contribution made</p>
              </div>
            </div>
            <div className="border-0 shadow p-6 rounded-xl bg-white flex items-center h-36">
              <div className="rounded-full w-14 h-14 bg-red-100 flex items-center justify-center mr-4">
                <img
                  src="/Images/Dashboard/moneys.svg"
                  alt="naira"
                  className="w-7 h-7"
                />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">
                  {formatMoney(
                    data?.data?.totalLoan ||
                      data?.data?.totalLoans ||
                      data?.data?.total_loans ||
                      0
                  )}
                </p>
                <p className="text-sm text-gray-500 mt-1">Total Loan disbursed</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-between w-2/5">
          <CardComp cardClassName="border-0 shadow p-4 rounded-xl bg-white">
            <h2 className="font-bold text-2xl mb-2 text-gray-800">
              Create custom push messages
            </h2>
            <button className="bg-[#8B313A] text-white px-4 py-2 rounded-md hover:bg-red-700">
              Create message
            </button>
          </CardComp>
          <CardComp cardClassName="border-0 shadow p-4 rounded-xl bg-white">
            <h2 className="font-bold text-2xl mb-2 text-gray-800">
              Push Messages
            </h2>
            <p className="text-sm text-gray-800 mb-2 font-semibold">
              Send push message to Ifeoluwa C&C and 3 others
            </p>
            <p className="text-xs text-gray-400 italic mb-2 w-4/5">
              So what are these strange lights in the sky? Are they aliens
              invading from Mars? Are the comets coming to start the next ice
              age?
            </p>
            <div className="flex justify-between items-center mt-3">
              <div className="flex -space-x-2">
                <img
                  src="/Images/Dashboard/image1.svg"
                  className="w-8 h-8 rounded-full border-2 border-white"
                  alt="user"
                />
                <img
                  src="/Images/Dashboard/image2.svg"
                  className="w-8 h-8 rounded-full border-2 border-white"
                  alt="user"
                />
                <img
                  src="/Images/Dashboard/image3.svg"
                  className="w-8 h-8 rounded-full border-2 border-white"
                  alt="user"
                />
              </div>
              <button
                className="bg-[#8B313A] text-white
                px-4 py-2 rounded hover:bg-red-700"
              >
                Send Message
              </button>
            </div>
          </CardComp>
        </div>
      </div>

      <div className="mt-5">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          Cooperatives Subscription
        </h2>
        <div className="flex space-x-6 pb-2 mb-4">
          <button
            onClick={() => setActiveSubscriptionTab("active")}
            className={`pb-2 ${
              activeSubscriptionTab === "active"
                ? "text-[#8B313A] font-semibold border-b-2 border-[#8B313A]"
                : "text-gray-500"
            }`}
          >
            Active subscription
          </button>
          <button
            onClick={() => setActiveSubscriptionTab("expiring")}
            className={`pb-2 ${
              activeSubscriptionTab === "expiring"
                ? "text-[#8B313A] font-semibold border-b-2 border-[#8B313A]"
                : "text-gray-500"
            }`}
          >
            Expiring soon
          </button>
          <button
            onClick={() => setActiveSubscriptionTab("expired")}
            className={`pb-2 ${
              activeSubscriptionTab === "expired"
                ? "text-[#8B313A] font-semibold border-b-2 border-[#8B313A]"
                : "text-gray-500"
            }`}
          >
            Expired subscriptions
          </button>
        </div>

        {activeSubscriptionTab === "active" &&
          (activeSubscriptionsLoading ? (
            <div className="py-8">
              <Loader
                size="md"
                color="primary"
                text="Loading active subscriptions..."
              />
            </div>
          ) : activeSubscriptions.length > 0 ? (
            renderSubscriptions(activeSubscriptions)
          ) : (
            <EmptyTable Message="No active subscriptions" />
          ))}
        {activeSubscriptionTab === "expiring" &&
          (expiringSubscriptionsLoading ? (
            <div className="py-8">
              <Loader
                size="md"
                color="primary"
                text="Loading expiring subscriptions..."
              />
            </div>
          ) : expiringSubscriptions.length > 0 ? (
            renderSubscriptions(expiringSubscriptions)
          ) : (
            <EmptyTable Message="No expiring subscriptions" />
          ))}
        {activeSubscriptionTab === "expired" &&
          (expiredSubscriptionsLoading ? (
            <div className="py-8">
              <Loader
                size="md"
                color="primary"
                text="Loading expired subscriptions..."
              />
            </div>
          ) : expiredSubscriptions.length > 0 ? (
            renderSubscriptions(expiredSubscriptions)
          ) : (
            <EmptyTable Message="No expired subscriptions" />
          ))}
      </div>
    </div>
  );
}
